import type { ReactNode } from 'react'

/**
 * How a point's `value` was derived, mirroring the GraphQL `TimeSeriesPointSource`
 * enum: `observed` = a real reading (value non-null); `computed` = carried forward
 * from an earlier reading (value null); `missing` = leading-edge gap (value null).
 * Absent for series that aren't normalized (e.g. single-channel), where a null
 * value is itself the "no data" signal.
 */
export type TimeSeriesPointSource = 'observed' | 'computed' | 'missing'

export enum SummaryMode {
  /** Cumulative level metric (e.g. Followers): carry forward the last value. */
  LATEST = 'latest',
  /** Per-day count (e.g. Posts): exact-day only, a gap is "no bar" not "no data". */
  TOTAL = 'total',
}

export enum MetricUnit {
  PERCENTAGE = 'percentage',
}

/** A single point in a metric's time series. */
export interface TimeSeriesDataPoint {
  date: string
  value?: number | null
  /** Real underlying date for a comparison-period point re-aligned onto the
   *  primary axis. Drives tooltip + axis labels. */
  originalDate?: string
  source?: TimeSeriesPointSource
}

/**
 * Semantic metric roles that map to a baked brand colour token (single-metric
 * charts). All-Channels series colour positionally instead (see `paletteIndex`).
 * `benchmark-channel`/`benchmark-median` colour the LinkedIn follower-growth-rate
 * view (the channel's own rate vs the cohort median reference line).
 */
export type MetricColorRole =
  | 'followers'
  | 'followers-net-new'
  | 'reach'
  | 'views'
  | 'posts'
  | 'benchmark-channel'
  | 'benchmark-median'

/**
 * A series as handed to {@link ChartSeriesProvider}. Note it carries no resolved
 * colour — the provider assigns colour from the baked palette, keyed by
 * `colorRole` (semantic) or `paletteIndex` (positional), so the line, tooltip,
 * and legend can't drift. A comparison sibling shares its primary's key.
 */
export interface ChartSeriesInput {
  /** Stable key; a comparison sibling ends in `-comparison`. */
  id: string
  /** Human label for tooltip + legend (e.g. channel name or metric name). */
  label: string
  /** Optional decorative glyph for tooltip + legend (channel/metric icon). */
  icon?: ReactNode
  unit?: MetricUnit
  summaryMode?: SummaryMode
  /** `comparison` renders dashed and is positionally aligned; defaults to `primary`. */
  kind?: 'primary' | 'comparison'
  /** A non-comparison reference line drawn dashed (e.g. the benchmark cohort
   *  median). Unlike a `comparison` sibling it keeps its own colour and stays in
   *  the tooltip/legend/data table. */
  dashed?: boolean
  /** Semantic colour role. Takes precedence over `paletteIndex`. */
  colorRole?: MetricColorRole
  /** Positional palette slot (All-Channels channels). Falls back to registration
   *  order among primaries when omitted. */
  paletteIndex?: number
  data: TimeSeriesDataPoint[]
}

/** A registry entry: the input series with its provider-resolved colour. */
export interface ChartSeries extends Omit<ChartSeriesInput, 'kind'> {
  kind: 'primary' | 'comparison'
  /** Resolved CSS colour token (a `var(--…)` string), assigned by the provider. */
  color: string
}

/**
 * A timeline annotation: a flag pinned to a date below the x-axis that, when
 * opened, reveals a dotted vertical line and a popover. The chart positions and
 * renders the flag; `content` is built by the caller so the chart stays agnostic
 * of what is being annotated.
 */
export interface ChartAnnotation {
  /** Stable key (e.g. the event day or bucket). */
  id: string
  /** Date the flag is anchored to, on the chart's x-axis. */
  date: string | Date
  /**
   * Dates to draw faint guide lines at while the flag is active. Use when one
   * flag stands in for several dates (a grouped bucket); defaults to `[date]`.
   */
  lineDates?: Array<string | Date>
  /**
   * Show the diagonal "no data" overlay across the region before `date` (chart
   * left edge → the flag) while this flag is open. Use when the series has no
   * data before that date (a single line); leave off when other series on the
   * chart do have earlier data (multi-line), where it would mislead.
   */
  showNoDataOverlay?: boolean
  /** Popover body shown when the flag is opened. */
  content: ReactNode
  /** Accessible label for the flag button. */
  ariaLabel: string
}
